import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import '../styles/Blog.css';

const Blog = ({ translations, language }) => {
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });

  const blogData = {
    en: {
      title: 'Blog & Insights',
      subtitle: 'Latest Marketing Trends & Strategies',
      posts: [
        {
          id: 1,
          title: 'The Future of AI in Digital Marketing',
          excerpt: 'Discover how artificial intelligence is revolutionizing marketing strategies and what it means for businesses in 2024.',
          category: 'AI Marketing',
          readTime: '5 min read',
          date: 'Dec 15, 2023',
          image: 'ai-marketing',
          tags: ['AI', 'Marketing', 'Future']
        },
        {
          id: 2,
          title: 'Mastering Arabic Market SEO',
          excerpt: 'Complete guide to optimizing your content for Arabic-speaking audiences and dominating Middle Eastern search results.',
          category: 'SEO',
          readTime: '8 min read',
          date: 'Dec 10, 2023',
          image: 'arabic-seo',
          tags: ['SEO', 'Arabic', 'Localization']
        },
        {
          id: 3,
          title: 'Social Commerce Revolution',
          excerpt: 'How social media platforms are becoming the new e-commerce giants and strategies to leverage this trend.',
          category: 'Social Media',
          readTime: '6 min read',
          date: 'Dec 5, 2023',
          image: 'social-commerce',
          tags: ['Social Media', 'E-commerce', 'Trends']
        },
        {
          id: 4,
          title: 'Data-Driven Marketing Analytics',
          excerpt: 'Advanced techniques for measuring marketing performance and making data-driven decisions that drive growth.',
          category: 'Analytics',
          readTime: '7 min read',
          date: 'Nov 28, 2023',
          image: 'analytics',
          tags: ['Analytics', 'Data', 'ROI']
        }
      ]
    },
    ar: {
      title: 'المدونة والرؤى',
      subtitle: 'أحدث اتجاهات واستراتيجيات التسويق',
      posts: [
        {
          id: 1,
          title: 'مستقبل الذكاء الاصطناعي في التسويق الرقمي',
          excerpt: 'اكتشف كيف يحدث الذكاء الاصطناعي ثورة في استراتيجيات التسويق وما يعنيه للشركات في 2024.',
          category: 'تسويق الذكاء الاصطناعي',
          readTime: '5 دقائق قراءة',
          date: '15 ديسمبر 2023',
          image: 'ai-marketing',
          tags: ['ذكاء اصطناعي', 'تسويق', 'مستقبل']
        },
        {
          id: 2,
          title: 'إتقان تحسين محركات البحث للسوق العربي',
          excerpt: 'دليل شامل لتحسين المحتوى للجماهير الناطقة بالعربية والهيمنة على نتائج البحث في الشرق الأوسط.',
          category: 'تحسين محركات البحث',
          readTime: '8 دقائق قراءة',
          date: '10 ديسمبر 2023',
          image: 'arabic-seo',
          tags: ['تحسين محركات البحث', 'عربي', 'توطين']
        },
        {
          id: 3,
          title: 'ثورة التجارة الاجتماعية',
          excerpt: 'كيف تصبح منصات وسائل التواصل الاجتماعي عمالقة التجارة الإلكترونية الجديدة واستراتيجيات الاستفادة من هذا الاتجاه.',
          category: 'وسائل التواصل الاجتماعي',
          readTime: '6 دقائق قراءة',
          date: '5 ديسمبر 2023',
          image: 'social-commerce',
          tags: ['وسائل التواصل الاجتماعي', 'التجارة الإلكترونية', 'اتجاهات']
        },
        {
          id: 4,
          title: 'تحليلات التسويق المدفوعة بالبيانات',
          excerpt: 'تقنيات متقدمة لقياس أداء التسويق واتخاذ قرارات مدفوعة بالبيانات تحفز النمو.',
          category: 'التحليلات',
          readTime: '7 دقائق قراءة',
          date: '28 نوفمبر 2023',
          image: 'analytics',
          tags: ['التحليلات', 'البيانات', 'عائد الاستثمار']
        }
      ]
    }
  };

  const data = blogData[language];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  return (
    <section id="blog" className="blog-section section" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title gradient-text">{data.title}</h2>
          <p className="section-subtitle">{data.subtitle}</p>
        </motion.div>

        <motion.div
          className="blog-grid"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {data.posts.map((post, index) => (
            <motion.article
              key={post.id}
              className="blog-card glass-morphism"
              variants={itemVariants}
              whileHover={{ y: -10 }}
            >
              <div className="blog-image">
                <div className={`blog-placeholder ${post.image}`}>
                  <i className="fas fa-newspaper"></i>
                </div>
                <div className="blog-category">{post.category}</div>
                <div className="blog-overlay">
                  <motion.button
                    className="read-btn"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <i className="fas fa-arrow-right"></i>
                  </motion.button>
                </div>
              </div>

              <div className="blog-content">
                <div className="blog-meta">
                  <span className="blog-date">
                    <i className="fas fa-calendar"></i>
                    {post.date}
                  </span>
                  <span className="blog-read-time">
                    <i className="fas fa-clock"></i>
                    {post.readTime}
                  </span>
                </div>

                <h3 className="blog-title">{post.title}</h3>
                <p className="blog-excerpt">{post.excerpt}</p>

                <div className="blog-tags">
                  {post.tags.map((tag, tagIndex) => (
                    <span key={tagIndex} className="blog-tag">
                      {tag}
                    </span>
                  ))}
                </div>

                <motion.button
                  className="blog-read-more"
                  whileHover={{ x: 5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {language === 'en' ? 'Read More' : 'اقرأ المزيد'}
                  <i className="fas fa-arrow-right"></i>
                </motion.button>
              </div>

              <div className="blog-glow"></div>
            </motion.article>
          ))}
        </motion.div>

        <motion.div
          className="blog-cta"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <motion.button
            className="btn-futuristic primary"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            {language === 'en' ? 'View All Articles' : 'عرض جميع المقالات'}
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Blog;